import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import Navbar from "../../nav_bar/Navbar";
import Footer from "../../footer/Footer";
import { fetchVocabularyList } from "./vocabularyService";
import "./VocabularyPage.css";

const difficultyOptions = [
  "BEGINNER",
  "ELEMENTARY",
  "INTERMEDIATE",
  "UPPER_INTERMEDIATE",
  "ADVANCED",
  "EXPERT",
];

const VocabularyPage = () => {
  const [words, setWords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [difficulty, setDifficulty] = useState("");
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const navigate = useNavigate();

  // Helper function to capitalize part of speech
  const capitalizePartOfSpeech = (pos) => {
    if (!pos) return pos;
    return pos.charAt(0).toUpperCase() + pos.slice(1).toLowerCase();
  };

  const loadWords = useCallback(async () => {
    setLoading(true);
    const res = await fetchVocabularyList({
      page,
      size: 20,
      search: search || undefined,
      difficulty: difficulty || undefined,
    });
    if (res.status === 200 && res.data?.status) {
      setWords(res.data.data || []);
      setTotalPages(res.data.totalPages || 0);
      setTotalItems(res.data.totalItems || 0);
    } else {
      setWords([]);
      setTotalPages(0);
      setTotalItems(0);
    }
    setLoading(false);
  }, [page, search, difficulty]);

  useEffect(() => {
    loadWords();
  }, [loadWords]);

  const handleFilterChange = (setter) => (e) => {
    setPage(0);
    setter(e.target.value);
  };

  const difficultyColor = (d) => {
    const map = {
      BEGINNER: "#4caf50",
      ELEMENTARY: "#8bc34a",
      INTERMEDIATE: "#ff9800",
      UPPER_INTERMEDIATE: "#ff5722",
      ADVANCED: "#e91e63",
      EXPERT: "#9c27b0",
    };
    return map[d?.toUpperCase()] || "#999";
  };

  const formatDifficulty = (d) =>
    d?.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) || d;

  return (
    <>
      <Navbar />
      <div className="vocab-page">
        <h1 className="vocab-title">📖 Vocabulary</h1>
        <p className="vocab-subtitle">
          Expand your word power, one word at a time.
        </p>

        <div className="vocab-filters">
          <input
            className="vocab-search"
            type="text"
            placeholder="Search words..."
            value={search}
            onChange={handleFilterChange(setSearch)}
          />

          <select
            className="vocab-filter-select"
            value={difficulty}
            onChange={handleFilterChange(setDifficulty)}
          >
            <option value="">All Levels</option>
            {difficultyOptions.map((opt) => (
              <option key={opt} value={opt}>
                {formatDifficulty(opt)}
              </option>
            ))}
          </select>
        </div>

        {totalItems > 0 && (
          <p className="vocab-count">{totalItems} words found</p>
        )}

        {loading ? (
          <p className="vocab-loading">Loading...</p>
        ) : words.length === 0 ? (
          <p className="vocab-empty">No words found.</p>
        ) : (
          <div className="vocab-grid">
            {words.map((w) => (
              <div
                key={w.id}
                className="vocab-card"
                onClick={() => navigate(`/study/vocabulary/${w.id}`)}
              >
                <div className="vocab-card-header">
                  <div>
                    <h3 className="vocab-word">{w.word}</h3>
                    {(w.pronunciation || w.phoneticSpelling) && (
                      <div className="vocab-pron-row">
                        {w.pronunciation && (
                          <span className="vocab-pronunciation">
                            <span className="vocab-pron-label">
                              IPA
                              <Link to="/study/ipa/lessons" className="vocab-ipa-info" title="Learn IPA" onClick={(e) => e.stopPropagation()}>
                                ℹ
                              </Link>
                            </span>
                            {w.pronunciation}
                          </span>
                        )}
                        {w.phoneticSpelling && (
                          <span className="vocab-phonetic">
                            <span className="vocab-pron-label">Say</span>
                            {w.phoneticSpelling}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <span
                    className="vocab-difficulty"
                    style={{ background: difficultyColor(w.difficulty) }}
                  >
                    {formatDifficulty(w.difficulty)}
                  </span>
                </div>

                {Array.isArray(w.partOfSpeech) && w.partOfSpeech.length > 0 ? (
                  <div style={{ marginTop: '6px' }}>
                    {w.partOfSpeech.map((pos, index) => (
                      <span key={index} className="vocab-pos" style={{ marginRight: '4px' }}>
                        {capitalizePartOfSpeech(pos)}
                      </span>
                    ))}
                  </div>
                ) : w.partOfSpeech ? (
                  <span className="vocab-pos">{capitalizePartOfSpeech(w.partOfSpeech)}</span>
                ) : null}

                {w.meaning && (
                  <p className="vocab-meaning">{w.meaning}</p>
                )}
              </div>
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <div className="vocab-pagination">
            <button disabled={page === 0} onClick={() => setPage(page - 1)}>
              ← Prev
            </button>
            <span>
              Page {page + 1} / {totalPages}
            </span>
            <button
              disabled={page + 1 >= totalPages}
              onClick={() => setPage(page + 1)}
            >
              Next →
            </button>
          </div>
        )}
      </div>
      <Footer />
    </>
  );
};

export default VocabularyPage;
